import get from 'lodash/get';
import { ComplexValue } from 'ui';
import { Value } from 'app/types-shared';
import { ValidationMessage, ValidationProps } from 'ui/common/validation/types';
import { DefaultValueReturnType } from 'app/components/access-panel/shared/types';

export const simpleFormNames = {
  optionsCollections: 'defaultValue_optionsCollections',
  caseTypes: 'defaultValue_caseTypes',
  values: 'defaultValue_values',
  valuesFields: 'defaultValue_valuesFields',
  defaultValue_caseTypes: 'defaultValue_caseTypes'
};

export interface ValidType<T> {
  valid: boolean;
  data: T;
}

export type CodeValue = ComplexValue<number>;
export interface DefaultValue {
  optionsColection: CodeValue;
  caseType: CodeValue;
  defaultValue: CodeValue;
  defaultValueFields: CodeValue[];
}

export const copyCodeValue = (codeValue: CodeValue): CodeValue => {
  if (!codeValue) {
    return undefined;
  }
  return {
    value: codeValue.value,
    description: codeValue.description
  };
};

export const readSelectionParams = (formValues: any): ValidType<DefaultValue> => {
  const optionsColection = get(formValues, `${simpleFormNames.optionsCollections}`);
  const caseType = get(formValues, `${simpleFormNames.caseTypes}`);
  const defaultValue = get(formValues, `${simpleFormNames.values}`);
  let defaultValueFields = [];
  const defaultValueFieldsValue = get(formValues, `${simpleFormNames.valuesFields}`);
  if (Array.isArray(defaultValueFieldsValue)) {
    defaultValueFields = defaultValueFieldsValue;
  }
  return {
    valid: !!optionsColection && !!caseType && !!defaultValue && !!defaultValueFields && !!defaultValueFields.length,
    data: {
      optionsColection: copyCodeValue(optionsColection),
      caseType: copyCodeValue(caseType),
      defaultValue: copyCodeValue(defaultValue),
      defaultValueFields: defaultValueFields.map(dv => copyCodeValue(dv))
    }
  };
};

export const selectDefaultValue = (defaultValueGroup: DefaultValue[], defaultValue: DefaultValue) => {
  return defaultValueGroup.find(value =>
    value.optionsColection.value === defaultValue.optionsColection.value &&
    value.caseType.value === defaultValue.caseType.value &&
    value.defaultValue.value === defaultValue.defaultValue.value);
};

const getAllFieldsForCaseType = (defaultValueGroup: DefaultValue[], caseType: ComplexValue<number>) => {

  return defaultValueGroup
    .filter(defaultValue => defaultValue.caseType.value === caseType.value)
    .reduce((result, current) => [
      ...result,
      ...current.defaultValueFields],
      [] as ComplexValue<number>[]);
};

export const getDuplicatedFields = (defaultValueGroup: DefaultValue[], defaultValue: DefaultValue) => {
  const existingFields = getAllFieldsForCaseType(defaultValueGroup, defaultValue.caseType);

  return defaultValue.defaultValueFields.filter(field =>
    existingFields.some(exisitingField => exisitingField.value === field.value)
  );
};

export const getUniqueFields = (defaultValueGroup: DefaultValue[], defaultValue: DefaultValue) => {
  const existingFields = getAllFieldsForCaseType(defaultValueGroup, defaultValue.caseType);

  return defaultValue.defaultValueFields.filter(field =>
    existingFields.every(existingField => existingField.value !== field.value)
  );
};

export const updateDefaultValue = (
  defaultValueGroup: DefaultValue[],
  defaultValue: DefaultValue,
  fields: ComplexValue<number>[]
) => {

  const exisitingDefaultValue = selectDefaultValue(defaultValueGroup, defaultValue);

  if (!fields || fields.length === 0) {
    return defaultValueGroup.filter(v => v !== exisitingDefaultValue);
  }

  const newDefaultValue = {
    optionsColection: defaultValue.optionsColection,
    caseType: defaultValue.caseType,
    defaultValue: defaultValue.defaultValue,
    defaultValueFields: fields
  };

  return exisitingDefaultValue
    ? defaultValueGroup.map(v => v === exisitingDefaultValue
      ? newDefaultValue
      : v)
    : [
      ...defaultValueGroup,
      newDefaultValue
    ];
};

const mapValueToComplexType = (valueCode: Value): CodeValue => {
  return {
    value: valueCode.v,
    description: valueCode.d
  };
};

export const mapToStore = (defaultValuesReturnTypes: DefaultValueReturnType[] = []): DefaultValue[] => {
  return defaultValuesReturnTypes.reduce((prev: DefaultValue[], curr: DefaultValueReturnType) => {
    const found = prev.findIndex(f =>
      f.optionsColection.value === curr.codeTable.v
      && f.caseType.value === curr.caseType.v
      && f.defaultValue.value === curr.defaultValue.v);
    if (found > -1) {
      const el = prev[found];
      prev[found] = {
        ...el,
        defaultValueFields: [...el.defaultValueFields, { value: curr.field.v, description: curr.field.d }]
      };
      return prev;
    }
    return [
      ...prev,
      {
        optionsColection: mapValueToComplexType(curr.codeTable),
        caseType: mapValueToComplexType(curr.caseType),
        defaultValue: mapValueToComplexType(curr.defaultValue),
        defaultValueFields: [mapValueToComplexType(curr.field)]
      }
    ];
  }, []);
};

export const createServerValidation =
  (defaultValueFields: CodeValue[], caseType: CodeValue, validation: { [key: string]: ValidationProps }): ValidationMessage[] => {
    const validationErrors: ValidationMessage[] = validation && Object.keys(validation)
      .map(propertyName => {
        const error = validation[propertyName];
        return error && error.messages;
      })
      .filter(f => !!f)
      .reduce((memo, valueKeys) => [...memo, ...valueKeys], []);

    const caseTypeValue = caseType && caseType.value;

    return defaultValueFields.map(defaultValueField => {
      const fieldValue = defaultValueField && defaultValueField.value;
      return validationErrors.find((error) =>
        error.parameters && error.parameters.field === fieldValue && error.parameters.field === fieldValue &&
        error.parameters.caseType === caseTypeValue);
    }).filter(f => !!f);
  };

export const getCurrentFieldId = (defaultValueFieldIds: number[], valueIds: number[]): number => {
  return defaultValueFieldIds
    .concat(valueIds)
    .find((val) =>
      !((defaultValueFieldIds as any).includes(val) && (valueIds as any).includes(val))
    );
};

export const checkFormValues = (formNames: string[], formValues: { [key: string]: any }) => {
  return (formNames && formValues)
    ? formNames
      .filter(item => (formValues[item] || []).length === 0)
      .length > 0
    : false;
};
